import React, { ReactNode } from 'react';
import { TaskCodes } from 'src/legacy/components/HomePage/OnboardingTasksSection/constants';
import {
  APP_SETUP_PAGE,
  CRM_CLIENTS_PAGE,
  CUSTOMIZATION_PAGE,
  DOMAIN_PAGE,
  PAYMENTS_PAGE,
} from 'src/constants';
import { ensureUnreachable } from 'src/utils/common_utils';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';

// represents Task detail info which is show when a task is made active on onboarding setup
export interface TaskDetailInfo {
  title: string;
  detailTitle?: string; // use if you want to use a different title in the task details
  description: ReactNode;
  buttonLabel?: string;
  illustrationName: string;
  learnMoreLink: string;
  routePath?: string;
}

interface DescriptionParams {
  currentUserEmail: string;
  portalName: string;
}

// Takes the task code and enrich the details for the given task
// enrichment includes having description text, illustration, learn more link and redirect path for the action button
export function MapTaskCodeToTaskDetails(
  taskCode: TaskCodes,
  isOnboardingTestClient: boolean,
  descriptionParams: DescriptionParams = {
    portalName: '',
    currentUserEmail: '',
  },
): TaskDetailInfo {
  switch (taskCode) {
    case TaskCodes.TaskCodeCustomization:
      return {
        title: 'Customize your portal',
        description:
          'Edit the look and feel of your portal to create a branded experience for your clients.',
        buttonLabel: 'Start customizing',
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/customization-and-setup#basic-customizations`,
        illustrationName: 'customize_your_portal',
        routePath: CUSTOMIZATION_PAGE.path,
      };
    case TaskCodes.TaskCodeFirstClient:
      if (isOnboardingTestClient) {
        return {
          title: 'Explore your portal',
          detailTitle: 'Explore the client experience',
          description: (
            <>
              We added <b>{descriptionParams.currentUserEmail}</b> as a test
              client. Check your inbox (or spam folder) for an email with
              subject{' '}
              <b>
                {descriptionParams.portalName} portal: Accept your invitation
              </b>{' '}
              and click on the link to try out the client experience.
            </>
          ),
          learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/test-run`,
          illustrationName: 'explore_your_portal',
        };
      }
      return {
        title: 'Create a test client',
        description:
          'For your first client, invite yourself to familiarize yourself with the client experience.',
        buttonLabel: `Create client`,
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/test-run`,
        illustrationName: 'create_your_first_client',
        routePath: `${CRM_CLIENTS_PAGE.path}?openCreateModal=true`,
      };
    case TaskCodes.TaskCodeFirstApp:
      return {
        title: 'Configure your apps',
        description:
          'Add apps like Calendly, Airtable, ClickUp, Jotform, Google Data Studio, Notion, and 100s of others.',
        buttonLabel: 'Add an app',
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/intro-to-apps`,
        illustrationName: 'add_your_first_app',
        routePath: `${APP_SETUP_PAGE.path}/new`,
      };
    case TaskCodes.TaskCodeCustomDomain:
      return {
        title: 'Connect custom domain',
        description:
          'Switch to a custom domain to make your portal accessible on a subdomain on your own website.',
        buttonLabel: 'Connect your domain',
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/custom-domains`,
        illustrationName: 'connect_your_custom_domain',
        routePath: DOMAIN_PAGE.path,
      };
    case TaskCodes.TaskCodeSetupWithPayments:
      return {
        title: 'Get setup with Payments',
        description:
          'Connect your bank or Stripe account so that you can start invoicing clients and get paid.',
        buttonLabel: 'Add a payout account',
        learnMoreLink: `https://www.${primaryDomainHostname.prod}/guide/copilot-billing-app`,
        illustrationName: 'get_setup_with_payments',
        routePath: PAYMENTS_PAGE.path,
      };
    default:
      ensureUnreachable(taskCode);
  }
}
